// 056 — Atención Adolescentes (Anexo 1 MSP, SNS-MSP/HCU-form. 056/2021). Transcripción campo por campo del PDF oficial (páginas
// 192-193, la 192 impresa invertida; spec en docs/hce-anexos/056-atencion-adolescentes.md), 18 bloques A-R. El bloque A (datos
// del paciente) lo resuelve `EncabezadoMspCampos`; aquí está el acompañante (A) y los bloques B-R.
//
// Equivalencia funcional, no facsímil. Decisiones:
// - Las rejillas de casillas SI / NO / N/C (D y E) son un selector de una sola marca por ítem (el papel no dice que sean
//   excluyentes, pero marcar dos respuestas al mismo ítem no tiene sentido). Los grupos donde varias opciones pueden ser
//   ciertas a la vez (acompañante, horas y razón de trabajo, imagen corporal, estado de ánimo, referente personal) se dejan
//   como casillas independientes.
// - F: el FAMILIOGRAMA es un recuadro de dibujo libre: no hay motor de dibujo, queda fuera (la pantalla lo dice). "Comparte
//   cama" no trae opciones impresas: Sí/No. "Tipo de relaciones sexuales" trae una casilla "NO" suelta: se ofrece como opción.
// - K: la fecha de última menstruación (AÑO/MES/DIA en tres celdas) es un solo campo de fecha, más "No conoce"/"No corresponde".
// - O: la leyenda pide marcar solo cuando hay patología (el ítem 1 "Aspecto normal" es la excepción del papel, se transcribe
//   igual). El ítem 16 Tanner trae Mamas / Genitales / Vello púbico sin estadios impresos: texto libre.
// - N: el IMC es un campo del papel (no se calcula solo). Perímetro cefálico e IMC no crean Observation (sin LOINC verificado).
// - R (profesional responsable): no se teclea (T6b, 2026-09-24) — el papel pide UN solo profesional; fecha, hora, nombres, apellidos y
//   número de documento los arma el servidor al firmar (firmante de la sesión + cédula del panel de firma). FIRMA y SELLO tampoco son campos.
// - FHIR (solo lo que tiene consumidor): `Encounter.reasonCode` (motivo), una `Condition` por diagnóstico (sin
//   `verificationStatus`; PRE/DEF solo en el PDF), el `Observation` de hallazgos del examen físico y los `Observation` de
//   signos vitales numéricos. Todo lo demás va como `Par[]` en el PDF firmado.
import type { CodeableConcept } from '@medplum/fhirtypes';
import { OPCIONES_SI_NO, OPCIONES_SI_NO_NC, paresDeCampos, valoresVacios, type CampoSimple, type OpcionCampo, type Valores } from './camposSimplesDatos';
import { diagnosticosVacios, paresDiagnosticos, codigosDiagnostico } from './bloquesAnamnesisDatos';
import type { DiagnosticoConMarca } from './MarcaDiagnostico';
import type { TomaSignos } from './signosVitalesToma';
import type { Par } from './index';

export interface Bloque056 {
  readonly clave: string;
  readonly titulo: string;
  readonly prefijo: string;
  readonly campos: readonly CampoSimple[];
  readonly columnas: 2 | 3 | 4;
}

const opc = (...etiquetas: string[]): OpcionCampo[] => etiquetas.map((e) => ({ valor: e, etiqueta: e }));
const texto = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta });
const numero = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'numero' });
const area = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'area' });
const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const lista = (clave: string, etiqueta: string, ...o: string[]): CampoSimple => ({ clave, etiqueta, tipo: 'select', opciones: opc(...o) });
const sn = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'select', opciones: OPCIONES_SI_NO });
const snc = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'select', opciones: OPCIONES_SI_NO_NC });
const clave = (rotulo: string): string =>
  rotulo
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');

const conRotulos = (fabrica: (c: string, e: string) => CampoSimple, prefijo: string, rotulos: readonly string[]): CampoSimple[] => rotulos.map((r) => fabrica(`${prefijo}_${clave(r)}`, r));

/** Grupo de casillas independientes (varias pueden marcarse a la vez): la etiqueta lleva el nombre del grupo. */
const conGrupo = (fabrica: (c: string, e: string) => CampoSimple, prefijo: string, grupo: string, rotulos: readonly string[]): CampoSimple[] =>
  rotulos.map((r) => fabrica(`${prefijo}_${clave(r)}`, `${grupo} — ${r}`));

const NIVELES_INSTRUCCION = ['Analfabeto', 'Inicial', 'Básica', 'Bachillerato', 'Técnico', 'Tercer nivel', 'Cuarto nivel'] as const;

/** Bloques en el orden del papel (A-R). Los diagnósticos (P) tienen su propio control (CIE + PRE/DEF). */
export const BLOQUES_056: readonly Bloque056[] = [
  {
    clave: 'acompanante',
    titulo: 'A. Acompañante',
    prefijo: 'acomp',
    columnas: 4,
    campos: [...conRotulos(casilla, 'acomp', ['No', 'Pareja', 'Madre', 'Padre', 'Amigo/a', 'Pariente', 'Otros']), texto('otrosTexto', 'Otros — especifique')],
  },
  { clave: 'motivo', titulo: 'B. Atención médica', prefijo: 'mot', columnas: 2, campos: [area('adolescente', 'Motivo de consulta (según adolescente)'), area('acompanante', 'Motivo de consulta (según acompañante)')] },
  { clave: 'enfermedad', titulo: 'C. Enfermedad actual', prefijo: 'enf', columnas: 2, campos: [area('enfermedadActual', 'Enfermedad actual')] },
  {
    clave: 'personales',
    titulo: 'D. Antecedentes patológicos y personales',
    prefijo: 'pers',
    columnas: 4,
    campos: [
      ...conRotulos(snc, 'p', [
        'Perinatales normales', 'Crecimiento normal', 'Desarrollo normal', 'Vacunas completas', 'Enfermedades crónicas', 'Enfermedades infecto contagiosas', 'Trastorno psicológico',
        'Uso de medicinas o sustancias', 'Accidentes intoxicación', 'Alergias', 'Infecciones recurrentes', 'Cirugía / hospitalización', 'Presunta violencia', 'Judiciales',
      ]),
      area('describa', 'Describa'),
    ],
  },
  {
    clave: 'familiares',
    titulo: 'E. Antecedentes patológicos familiares',
    prefijo: 'fam',
    columnas: 4,
    campos: [...conRotulos(snc, 'f', ['Diabetes', 'Obesidad', 'Alergias', 'Cardio vasculares', 'Cáncer', 'Infecciones', 'Alcohol / drogas', 'Violencia familiar', 'Madre / padre adolescente', 'Judiciales']), area('describa', 'Describa')],
  },
  { clave: 'convive', titulo: 'F. Datos entorno familiar — Convive con', prefijo: 'conv', columnas: 4, campos: conRotulos(sn, 'c', ['Madre', 'Padre', 'Madrastra', 'Padrastro', 'Hermanos', 'Pareja', 'Hijo/a', 'Solo', 'Otros']) },
  { clave: 'vive', titulo: 'F. Datos entorno familiar — Vive', prefijo: 'vive', columnas: 3, campos: conRotulos(sn, 'v', ['En institución', 'En la calle', 'Casa']) },
  {
    clave: 'instruccion',
    titulo: 'F. Datos entorno familiar — Nivel de instrucción',
    prefijo: 'instr',
    columnas: 2,
    campos: [lista('padre', 'Padre o sustituto', ...NIVELES_INSTRUCCION), lista('madre', 'Madre o sustituta', ...NIVELES_INSTRUCCION)],
  },
  {
    clave: 'trabajoFamilia',
    titulo: 'F. Datos entorno familiar — Trabajo',
    prefijo: 'tfam',
    columnas: 2,
    campos: [lista('padre', 'Padre o sustituto', 'Ninguno', 'Estable', 'No estable'), lista('madre', 'Madre o sustituta', 'Ninguno', 'Estable', 'No estable'), texto('especifique', 'Especifique')],
  },
  { clave: 'percepcion', titulo: 'F. Datos entorno familiar — Percepción familiar del adolescente', prefijo: 'perc', columnas: 3, campos: [lista('percepcion', 'Percepción familiar del adolescente', 'Buena', 'Regular', 'Mala', 'No hay'), texto('observaciones', 'Observaciones (familiograma)')] },
  {
    clave: 'vivienda',
    titulo: 'F. Datos entorno familiar — Vivienda',
    prefijo: 'viv',
    columnas: 3,
    campos: [
      sn('energiaElectrica', 'Energía eléctrica'),
      lista('agua', 'Agua', 'En hogar', 'Fuera hogar'),
      lista('excretas', 'Excretas', 'En hogar', 'Fuera hogar'),
      numero('cuartos', 'Número de cuartos para dormir'),
      numero('personas', 'Número de personas que habitan'),
      sn('comparteCama', 'Comparte cama'),
      area('observaciones', 'Observaciones'),
    ],
  },
  {
    clave: 'educacion',
    titulo: 'G. Datos sobre la educación',
    prefijo: 'edu',
    columnas: 3,
    campos: [
      sn('estudia', 'Estudia'),
      texto('institucion', 'Nombre institución educativa'),
      lista('nivel', 'Nivel en curso', 'Ninguno', 'Básico', 'Bachillerato', 'Tercer nivel'),
      texto('grado', 'Grado / curso'),
      sn('violenciaEscolar', 'Presunta violencia / abuso escolar'),
      numero('aniosRepetidos', 'Años repetidos'),
      texto('causasRepetidos', 'Años repetidos — causas'),
      sn('desercion', 'Deserción / exclusión'),
      texto('causasDesercion', 'Deserción / exclusión — causas'),
      texto('observaciones', 'Observaciones'),
    ],
  },
  {
    clave: 'trabajo',
    titulo: 'H. Datos sobre el trabajo',
    prefijo: 'trab',
    columnas: 3,
    campos: [
      lista('actividad', 'Actividad', 'Trabaja', 'Busca por primera vez', 'No y no busca', 'Desocupado'),
      numero('edadInicio', 'Edad inicio trabajo (años)'),
      numero('horasSemana', 'Trabajo (horas por semana)'),
      ...conRotulos(casilla, 'horas', ['Mañana', 'Fin de semana', 'Noche', 'Tarde', 'Todo el día', 'N/C']).map((c) => ({ ...c, etiqueta: `Horas de trabajo — ${c.etiqueta}` })),
      ...conRotulos(casilla, 'razon', ['Económica', 'Me gusta', 'Autonomía', 'N/C']).map((c) => ({ ...c, etiqueta: `Razón del trabajo — ${c.etiqueta}` })),
      snc('legalizado', 'Trabajo legalizado'),
      snc('insalubre', 'Trabajo insalubre'),
      texto('observaciones', 'Observaciones'),
    ],
  },
  {
    clave: 'vidaSocial',
    titulo: 'I. Datos sobre vida social',
    prefijo: 'soc',
    columnas: 3,
    campos: [
      lista('aceptacion', 'Aceptación', 'Aceptado', 'Rechazado', 'Ignorado', 'No conoce'),
      ...['Pareja', 'Amigos', 'Actividad grupal', 'Deporte', 'Televisión', 'Computador', 'Redes sociales', 'Celular', 'Videojuego', 'Otras actividades'].map((r) => sn(`a_${clave(r)}`, r)),
      texto('otrasCuales', 'Otras actividades — cuáles'),
      ...['Actividad grupal', 'Deporte', 'Televisión', 'Computador', 'Redes sociales', 'Celular', 'Videojuego', 'Otras actividades'].map((r) => numero(`h_${clave(r)}`, `Horas por día — ${r}`)),
      texto('observaciones', 'Observaciones'),
    ],
  },
  {
    clave: 'habitos',
    titulo: 'J. Hábitos',
    prefijo: 'hab',
    columnas: 3,
    campos: [
      lista('sueno', 'Horas de sueño', 'Menos 8 horas', 'Más 8 horas'),
      sn('alimentacion', 'Alimentación saludable'),
      sn('comidasFamilia', 'Comidas por día con la familia'),
      numero('comidasCuantas', 'Comidas por día con la familia — cuántas'),
      sn('tabaco', 'Tabaco'),
      numero('tabacoCuantos', 'Tabaco — cuántos'),
      texto('tabacoFrecuencia', 'Tabaco — frecuencia'),
      numero('tabacoEdad', 'Edad inicio tabaco (años)'),
      sn('alcohol', 'Alcohol'),
      numero('alcoholEdad', 'Edad inicio alcohol (años)'),
      texto('alcoholFrecuencia', 'Alcohol — frecuencia'),
      sn('otrasSustancias', 'Otras sustancias'),
      texto('sustanciaCual', 'Otras sustancias — cuál'),
      texto('sustanciaFrecuencia', 'Otras sustancias — frecuencia'),
      sn('conduce', 'Conduce vehículo'),
      texto('conduceCual', 'Conduce vehículo — cuál'),
      texto('observaciones', 'Observaciones'),
    ],
  },
  {
    clave: 'ginecologico',
    titulo: 'K. Antecedentes gineco-urológico',
    prefijo: 'gin',
    columnas: 3,
    campos: [
      numero('menarquiaEdad', 'Menarquia / espermarquia — edad'),
      casilla('menarquiaNoConoce', 'Menarquia / espermarquia — no conoce'),
      { clave: 'fum', etiqueta: 'Fecha última menstruación', tipo: 'fecha' },
      casilla('fumNoConoce', 'Fecha última menstruación — no conoce'),
      casilla('fumNoCorresponde', 'Fecha última menstruación — no corresponde'),
      snc('ciclosRegulares', 'Ciclos regulares'),
      snc('dismenorrea', 'Dismenorrea'),
      sn('secrecionGenital', 'Secreción genital'),
      sn('its', 'Infecciones de transmisión sexual'),
      texto('itsEspecifique', 'Infecciones de transmisión sexual — especifique'),
      numero('embarazos', 'Embarazos'),
      numero('partos', 'Partos'),
      numero('cesareas', 'Cesáreas'),
      numero('abortos', 'Abortos'),
      numero('hijosVivos', 'Hijos vivos'),
      numero('hijosMuertos', 'Hijos muertos'),
      texto('observaciones', 'Observaciones'),
    ],
  },
  {
    clave: 'sexualidad',
    titulo: 'L. Sexualidad',
    prefijo: 'sex',
    columnas: 3,
    campos: [
      sn('asesoria', 'Necesita asesoría'),
      lista('tipoRelaciones', 'Tipo relaciones sexuales', 'No', 'Homosexual', 'Bisexual', 'Heterosexual'),
      lista('parejaActual', 'Pareja actual', 'Única', 'Varias', 'N/C'),
      numero('edadInicio', 'Edad inicio relaciones sexuales (años)'),
      snc('voluntarias', 'Voluntarias'),
      snc('disfuncion', 'Disfunción sexual'),
      sn('metodo', 'Método anticonceptivo'),
      texto('metodoCual', 'Método anticonceptivo — cuál'),
      lista('apoyoPareja', 'Apoyo de pareja', 'Siempre', 'Nunca', 'A veces'),
      snc('violenciaSexual', 'Presunta violencia sexual'),
      texto('observaciones', 'Observaciones'),
    ],
  },
  {
    clave: 'psicoemocional',
    titulo: 'M. Situación psicoemocional',
    prefijo: 'psi',
    columnas: 3,
    campos: [
      ...conGrupo(casilla, 'imagen', 'Imagen corporal', ['Conforme', 'Impide relación con demás', 'Crea preocupación']),
      ...conGrupo(casilla, 'animo', 'Estado de ánimo (salud mental)', ['Bueno', 'Triste', 'Nervioso', 'Alegre', 'Muy tímido', 'Otro']),
      ...conGrupo(casilla, 'referente', 'Referente personal', ['Madre', 'Otro familiar', 'Ninguno', 'Padre', 'Fuera del hogar']),
      lista('proyectoVida', 'Proyecto de vida', 'Claro', 'Ninguno', 'Confuso'),
      texto('observaciones', 'Observaciones'),
    ],
  },
  {
    clave: 'constantes',
    titulo: 'N. Constantes vitales y antropometría',
    prefijo: 'cv',
    columnas: 3,
    campos: [
      texto('temperatura', 'Temperatura (°C)'),
      texto('presion', 'Presión arterial (mmHg)'),
      texto('pulso', 'Pulso (/min)'),
      texto('fr', 'Frecuencia respiratoria (/min)'),
      texto('peso', 'Peso (kg)'),
      texto('talla', 'Talla (cm)'),
      texto('imc', 'IMC (kg/m²)'),
      texto('perimetroCefalico', 'Perímetro cefálico (cm)'),
      texto('spo2', 'Pulsioximetría (%)'),
    ],
  },
  {
    clave: 'examen',
    titulo: 'O. Examen físico (marcar cuando presente patología y describa)',
    prefijo: 'ef',
    columnas: 3,
    campos: [
      ...[
        'Aspecto normal', 'Piel y faneras', 'Agudeza visual', 'Agudeza auditiva', 'Boca y dientes', 'Cuello y tiroides', 'Tórax y mamas', 'Cardio pulmonar',
        'Columna vertebral', 'Abdomen', 'Genito urinario', 'Ano - rectal', 'Miembros superiores', 'Miembros inferiores', 'Neurológico',
      ].map((r, i) => casilla(`e${i + 1}`, `${i + 1}. ${r}`)),
      texto('tannerMamas', '16. Tanner — mamas'),
      texto('tannerGenitales', '16. Tanner — genitales'),
      texto('tannerVello', '16. Tanner — vello púbico'),
      area('observaciones', 'Observaciones'),
    ],
  },
  { clave: 'plan', titulo: 'Q. Plan de tratamiento (de diagnóstico, terapéuticos, educacionales y rehabilitación)', prefijo: 'plan', columnas: 2, campos: [area('plan', 'Plan de tratamiento')] },
];

export const NUM_DIAGNOSTICOS_056 = 6;

// ---------- Estado ----------

export interface Estado056 {
  readonly bloques: Readonly<Record<string, Valores>>;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
}

export function estadoInicial056(): Estado056 {
  return { bloques: Object.fromEntries(BLOQUES_056.map((b) => [b.clave, valoresVacios(b.campos)])), diagnosticos: diagnosticosVacios() };
}

const valor = (e: Estado056, bloque: string, campo: string): string => (e.bloques[bloque]?.[campo] ?? '').trim();

// ---------- FHIR ----------

/** `Encounter.reasonCode`: el motivo según el adolescente y, si hay, según el acompañante. */
export function motivoDe056(e: Estado056): string {
  return [valor(e, 'motivo', 'adolescente'), valor(e, 'motivo', 'acompanante')].filter(Boolean).join(' — ');
}

export const codigosDiagnostico056 = (e: Estado056): CodeableConcept[] => codigosDiagnostico(e.diagnosticos);

/** Texto de "Hallazgos de examen físico": lo marcado (número + rótulo), Tanner y las observaciones. */
export function hallazgosDe056(e: Estado056): string {
  const b = BLOQUES_056.find((x) => x.clave === 'examen')!;
  const v = e.bloques.examen ?? {};
  const marcados = b.campos.filter((c) => c.tipo === 'casilla' && v[c.clave] === 'true').map((c) => c.etiqueta);
  const tanner = ['tannerMamas', 'tannerGenitales', 'tannerVello']
    .map((k) => [b.campos.find((c) => c.clave === k)!.etiqueta, (v[k] ?? '').trim()] as const)
    .filter(([, t]) => t !== '')
    .map(([etiqueta, t]) => `${etiqueta}: ${t}`);
  return [marcados.length ? `Patología: ${marcados.join(', ')}` : '', tanner.join('; '), (v.observaciones ?? '').trim()].filter(Boolean).join('. ');
}

/** Signos vitales para los `Observation` (solo se crean los que se leen como número). */
export function tomaDe056(e: Estado056): TomaSignos {
  const c = e.bloques.constantes ?? {};
  return { temperatura: c.temperatura, presion: c.presion, fc: c.pulso, fr: c.fr, peso: c.peso, talla: c.talla, spo2: c.spo2 };
}

// ---------- PDF ----------

/** Serializa el formulario a `Par[]` para `onGuardado` (PDF firmado) — filtra respuestas vacías. */
export function paresDe056(e: Estado056): Par[] {
  const pares: Par[] = [];
  for (const b of BLOQUES_056) {
    pares.push(...paresDeCampos(b.campos, e.bloques[b.clave] ?? {}, `${b.titulo} — `));
    if (b.clave === 'examen') pares.push(...paresDiagnosticos(e.diagnosticos).map((p) => ({ ...p, pregunta: `P. Diagnóstico — ${p.pregunta}` })));
  }
  return pares.filter((p) => p.respuesta.trim() !== '');
}
